/**
 * Portfolio period: current year or one quarter. Not a plan type.
 */

import React from 'react';
import { currentCalendarYear, type PortfolioView } from './types';

interface PortfolioPeriodControlsProps {
  view: PortfolioView;
  saving: boolean;
  onChange: (next: PortfolioView) => void;
  t: (key: string) => string;
}

function CheckRow({
  label,
  checked,
  disabled,
  onPick
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  return (
    <label className="flex items-center gap-1 text-[11px] text-[var(--color-surface-text)] cursor-pointer">
      <input type="checkbox" className="mt-0" disabled={disabled} checked={checked} onChange={onPick} />
      <span className="leading-tight">{label}</span>
    </label>
  );
}

export const PortfolioPeriodControls: React.FC<PortfolioPeriodControlsProps> = ({
  view,
  saving,
  onChange,
  t
}) => {
  const year = currentCalendarYear();
  const quarterLabels: Record<number, string> = {
    1: t('kpi.perfQuarter1') || 'Q1',
    2: t('kpi.perfQuarter2') || 'Q2',
    3: t('kpi.perfQuarter3') || 'Q3',
    4: t('kpi.perfQuarter4') || 'Q4'
  };

  return (
    <div className="mb-2">
      <h3 className="text-xs font-medium text-[var(--color-surface-text)] mb-1">
        {t('kpi.perfPortfolioPeriod') || 'Period'}
      </h3>
      <p className="text-[10px] font-medium text-[var(--color-secondary-text)] mb-0.5">
        {t('kpi.perfPortfolioCurrent') || 'Current'}
      </p>
      <CheckRow
        label={String(year)}
        checked={view.mode === 'year'}
        disabled={saving}
        onPick={() => onChange({ mode: 'year', year, quarter: view.quarter })}
      />
      <p className="text-[10px] font-medium text-[var(--color-secondary-text)] mt-1.5 mb-0.5">
        {t('kpi.perfHorizonQuarterly') || 'Quarterly'}
      </p>
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
        {[1, 2, 3, 4].map((quarter) => (
          <CheckRow
            key={quarter}
            label={quarterLabels[quarter] || `Q${quarter}`}
            checked={view.mode === 'quarter' && view.quarter === quarter}
            disabled={saving}
            onPick={() => onChange({ mode: 'quarter', year, quarter })}
          />
        ))}
      </div>
    </div>
  );
};
